/**
 * `/about-us/history/`: Ryerson's company timeline. On any other `NEXT_PUBLIC_COMPANY` the page
 * isn't built. Copy comes from `src/content/ryerson/about-us/history/index.ts` (`PageContent` is
 * its site-search entry); the only build-time fetch is the header and footer data.
 *
 * Each `TimelineGroup` (an era) becomes one `components/Shared/AllSections/AllSections.tsx`
 * section, rendered by `components/History/Timeline/Timeline.tsx`, plus one item in the
 * desktop-only `SubNavigation` bar labelled with the era's year range.
 *
 * Rules a change must keep:
 * - **Sub-navigation ids match the section anchors.** Both are `"<title> (<yearRange>)"`
 *   lower-cased with whitespace runs replaced by `-`, the expression `AllSections` applies to each
 *   section title. Change one and the other together, or the sub-navigation links stop landing on
 *   their era.
 *
 * On desktop, `alignment` carries the side of each era's first card from one group to the next.
 * It continues the zig-zag except after an era that starts "right" with an odd card count, where
 * the next era starts on the same side as that era's last card; see the comment at the
 * alternation below.
 *
 * `src/unit-test/AboutUs/History.spec.tsx` renders the page in English as a smoke test.
 */
import React from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import { useApplication } from "@ryerson/frontend.application";
import HistoryHero from "@components/History/Hero/Hero";
import TimelineComponent, { TimelineGroup } from "@components/History/Timeline/Timeline";
import MetaHead from "@components/Shared/model/MetaHead";
import { useHasMounted } from "@components/Hooks";
import { TimelineContent, MetaObject, HeroContent } from "@content/ryerson/about-us/history";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { useRouter } from "next/router";

/**
 * One sub-navigation link: `id` is the section anchor, `title` the label shown (the year
 * range).
 */
type NavigableItem = {
	id: string;
	title: string;
};

type SubNavigationProps = {
	content: NavigableItem[];
};

const NavigationContainer = styled.div`
	width: 100%;
	height: 56px;
	line-height: 56px;
`;

const NavigationItem = styled.div`
	display: inline-block;
	vertical-align: middle;
	height: 100%;
	margin-right: 40px;
`;

const Divider = styled.hr`
	opacity: 0.1;
	margin-top: -1px;
	margin-bottom: 0px;
`;

/**
 * The desktop bar of era links. Each link navigates through the Next router to the current page
 * path with `hash` set to the era's section id, which `AllSections` scrolls to.
 */
const SubNavigation: React.FC<SubNavigationProps> = ({ content }) => {
	const { theme } = useTheme();
	const router = useRouter();

	return (
		<>
			<ContentSection type="primary" vPadding="0">
				<NavigationContainer>
					{content.map((item: NavigableItem) => {
						return (
							<Link
								next={true}
								to={`${router.pathname}`}
								hash={item.id}
								key={item.id}
							>
								<NavigationItem
									css={css`
										&:hover {
											border-bottom: 1px solid
												${theme.colors.primary.primaryBrand};
										}
									`}
								>
									<Typography
										size="md"
										css={css`
											text-decoration: none;
										`}
										color={theme.colors.primary.header}
										weight="bold"
									>
										{item.title}
									</Typography>
								</NavigationItem>
							</Link>
						);
					})}
				</NavigationContainer>
			</ContentSection>
			<Divider
				css={css`
					color: ${theme.colors.primary.darkerGray};
				`}
			/>
		</>
	);
};

/** Ryerson build only. Fetches the header and footer data at build time. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** Builds one section and one sub-navigation item per era of the current language's timeline. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	if (!useHasMounted()) return null;

	const sections: Sections[] = [];
	const timeline = TimelineContent[language];
	const subNav: NavigableItem[] = [];
	if (timeline.length && timeline.length > 0) {
		let alignment: any = "left";
		timeline.forEach((tl: TimelineGroup, index: number) => {
			sections.push({
				title: tl.title + " (" + tl.yearRange + ")",
				maxHeight: "15000px",
				cmp: (
					<TimelineComponent
						content={tl}
						type={index % 2 === 0 ? "primary" : "secondary"}
						lastGroup={index === timeline.length - 1 ? true : false}
						firstGroup={index === 0 ? true : false}
						startingAlignment={alignment}
					/>
				),
			});
			subNav.push({
				id: (tl.title + " (" + tl.yearRange + ")").toLowerCase().replace(/\s+/g, "-"),
				title: tl.yearRange,
			});
			// Picks the side of the next era's first card. From "left", an odd card count ends on
			// the left, so the next era starts "right". From "right", an even count ends on the
			// left and sets "right" again; an odd count ends on the right but leaves `alignment`
			// at "right", so the next era's first card sits on the same side as this era's last.
			// Once "right", the value never returns to "left". With today's copy (4, 5, 5 and 20
			// cards) that happens between the 1960-2009 era and the 2010-present era.
			if (alignment === "left") {
				if (tl.cards.length % 2 !== 0) alignment = "right";
			} else {
				if (tl.cards.length % 2 === 0) alignment = "right";
			}
		});
	}
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<HistoryHero content={HeroContent[language]} />
			<Media greaterThanOrEqual="lg">
				<SubNavigation content={subNav} />
			</Media>
			<AllSections sections={sections} />
		</HeaderLayout>
	);
};

export default Page;
